import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import RichTextEditor from "../../../../common/FormComponents/RichTextEditor";
import { ConfirmDialog } from "../../EventSales/eventSalesShared";
import {
  EventSponsorshipAddOn,
  EventSponsorshipPackage,
  EventTicketType,
} from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardStepProps,
  moveWizardRow,
  wizardCurrencyAdornmentClassName,
  wizardCurrencyInputClassName,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import { SectionShell } from "../../../../common/dashboard/SectionShell";
import { CurrencyInput } from "../../../../common/dashboard/CurrencyInput";
import { formatCurrencyValueOnBlur } from "../../../../common/dashboard/currencyUnits";

const greenSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#10B981",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#10B981",
  },
};

const formatDollars = (value?: string): string => {
  const numeric = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return `$${(Number.isFinite(numeric) ? numeric : 0).toFixed(2)}`;
};

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const findNoChargeSponsorshipTicket = (
  tickets: EventTicketType[]
): EventTicketType | undefined => tickets.find((ticket) => ticket.code === "NCST");

const blankPackage = (tickets: EventTicketType[]): EventSponsorshipPackage => ({
  id: wizardId("sponsor"),
  name: "",
  description: "",
  price: "",
  maxAvailable: "0",
  includedTickets: "0",
  publicDisplayEnabled: true,
  code: "",
  heroBannerEnabled: false,
  additionalBenefits: "",
  addOns: [],
  includedTicketTypeId: findNoChargeSponsorshipTicket(tickets)?.id || "",
});

const totalAllocated = (pkg: EventSponsorshipPackage) => {
  const available = Number(pkg.maxAvailable) || 0;
  const included = Number(pkg.includedTickets) || 0;
  return available * included;
};

const QuantityStepper = ({
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}): React.ReactElement => {
  const current = Number(value) || 0;
  return (
    <div
      className={`flex h-[58px] items-center justify-between rounded-[6px] border border-gray-300 px-1 ${
        disabled ? "bg-gray-50" : "bg-white"
      }`}
    >
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={`Decrease ${ariaLabel}`}
        onClick={() => onChange(String(Math.max(0, current - 1)))}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <span
        className={`min-w-[32px] text-center text-[16px] font-bold ${
          disabled ? "text-gray-400" : "text-gray-950"
        }`}
      >
        {current}
      </span>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={`Increase ${ariaLabel}`}
        onClick={() => onChange(String(current + 1))}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const Step4SponsorSetup = ({
  event,
  setEvent,
}: WizardStepProps): React.ReactElement => {
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));

  const packages = event.sponsorshipPackages;
  const [draft, setDraft] = useState<EventSponsorshipPackage | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventSponsorshipPackage | null>(null);
  const [addOnEntry, setAddOnEntry] = useState<EventSponsorshipAddOn | null>(null);
  const [heroLimitBlocked, setHeroLimitBlocked] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // Both step-4 tables show six-dot drag handles — rows reorder by dragging.
  const [levelDragIndex, setLevelDragIndex] = useState<number | null>(null);
  const [addOnDragIndex, setAddOnDragIndex] = useState<number | null>(null);
  const dropLevelRow = (targetIndex: number) => {
    if (levelDragIndex === null || levelDragIndex === targetIndex) {
      setLevelDragIndex(null);
      return;
    }
    update(
      "sponsorshipPackages",
      moveWizardRow(packages, levelDragIndex, targetIndex)
    );
    setLevelDragIndex(null);
  };

  // Guard: the backend drops saved sponsorship levels once sponsorship is
  // disabled, so confirm before turning the toggle off while levels exist.
  const handleSponsorshipToggle = (checked: boolean) => {
    if (!checked && packages.length > 0) {
      setConfirmDisableOpen(true);
      return;
    }
    update("sponsorshipEnabled", checked);
  };

  const closeDraft = () => {
    setDraft(null);
    setAddOnEntry(null);
    setHeroLimitBlocked(false);
    setShowValidation(false);
  };
  const openAdd = () => {
    setDraft(blankPackage(event.tickets));
    setIsNew(true);
  };
  const openEdit = (pkg: EventSponsorshipPackage) => {
    setDraft({
      ...pkg,
      // July 9, 2026 direction: sponsorships may only assign the No Charge
      // Sponsorship Ticket, so the assignment is forced to NCST (or cleared
      // when the event no longer has one) rather than kept from older data.
      includedTicketTypeId: findNoChargeSponsorshipTicket(event.tickets)?.id || "",
      addOns: (pkg.addOns || []).map((addOn) => ({ ...addOn })),
    });
    setIsNew(false);
  };
  const patchDraft = (patch: Partial<EventSponsorshipPackage>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const draftAddOns = draft?.addOns || [];
  const dropAddOnRow = (targetIndex: number) => {
    if (addOnDragIndex === null || addOnDragIndex === targetIndex) {
      setAddOnDragIndex(null);
      return;
    }
    patchDraft({ addOns: moveWizardRow(draftAddOns, addOnDragIndex, targetIndex) });
    setAddOnDragIndex(null);
  };
  const assignedTicket = event.tickets.find(
    (ticket) => ticket.id === draft?.includedTicketTypeId
  );

  // Guard: only one sponsor level may appear on the registration hero banner.
  const handleHeroBannerToggle = (checked: boolean) => {
    if (checked && packages.some((pkg) => pkg.heroBannerEnabled && pkg.id !== draft?.id)) {
      setHeroLimitBlocked(true);
      return;
    }
    setHeroLimitBlocked(false);
    patchDraft({ heroBannerEnabled: checked });
  };

  const saveAddOnEntry = () => {
    if (!addOnEntry || !addOnEntry.description.trim()) {
      return;
    }
    patchDraft({ addOns: [...draftAddOns, addOnEntry] });
    setAddOnEntry(null);
  };

  const draftValid = Boolean(
    draft &&
      Number(draft.maxAvailable) > 0 &&
      draft.code?.trim() &&
      draft.name.trim() &&
      draft.price.trim() &&
      stripHtml(draft.additionalBenefits || "")
  );

  const saveDraft = () => {
    if (!draft) {
      return;
    }
    if (!draftValid) {
      setShowValidation(true);
      return;
    }
    // POL-005: keep the canonical decimal-dollars shape ("500.00", no "$") in
    // form state; formatDollars stays for read-only display sites only.
    // A quantity without an assignable NCST ticket would save orphaned
    // includedTickets, so it zeroes out when no ticket type is assigned.
    const normalized = {
      ...draft,
      price: formatCurrencyValueOnBlur(draft.price),
      includedTickets: draft.includedTicketTypeId ? draft.includedTickets : "0",
    };
    update(
      "sponsorshipPackages",
      isNew
        ? [...packages, normalized]
        : packages.map((pkg) => (pkg.id === normalized.id ? normalized : pkg))
    );
    closeDraft();
  };

  return (
    <>
      <div className="flex items-center gap-4 px-1">
        <Switch
          checked={event.sponsorshipEnabled}
          onChange={({ target }) => handleSponsorshipToggle(target.checked)}
          inputProps={{
            "aria-label": "Enable sponsorship opportunities for this event",
          }}
          sx={greenSwitchSx}
        />
        <div>
          <p className="text-[18px] font-extrabold leading-6 text-black">
            Enable sponsorship opportunities for this event
          </p>
          <p className="mt-1 text-[16px] leading-6 text-black">
            When enabled, sponsorship packages will be displayed on the event page and the number of spots available for each package
          </p>
        </div>
      </div>

      {event.sponsorshipEnabled ? (
        <SectionShell
          id="event-sponsorship-levels"
          variant="pill"
          title="Create Sponsorship Levels"
          description="Create various sponsorship packages for this event and include sponsor benefits by clicking on Add Sponsor Level"
        >
          <div className="flex items-center gap-5">
            <WizardActionButton tone="gold" startIcon={<AddIcon />} onClick={openAdd}>
              Add Sponsor Level
            </WizardActionButton>
            <div className="h-[2px] flex-1 bg-black" aria-hidden="true" />
          </div>

          <div className="mt-14">
            <h3 className="text-[22px] font-extrabold leading-tight text-gray-950">
              View Created Sponsor Levels
            </h3>
            <p className="mt-2 text-[18px] leading-7 text-gray-950">
              View and edit the created sponsorship levels for this event. (availability, price and sponsor benefits)
            </p>
          </div>
          <TableContainer className="mt-6 rounded border border-gray-200">
            <Table size="small">
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="!w-10" />
                  <TableCell className="!font-bold uppercase">Code</TableCell>
                  <TableCell className="!font-bold uppercase">Sponsorship Level</TableCell>
                  <TableCell className="!font-bold uppercase" align="center">
                    Tickets Included
                  </TableCell>
                  <TableCell className="!font-bold uppercase">Sponsor Price</TableCell>
                  <TableCell className="!font-bold uppercase" align="center">
                    Available Spots
                  </TableCell>
                  <TableCell className="!font-bold uppercase" align="center">
                    Total Tickets Allocated
                  </TableCell>
                  <TableCell className="!font-bold uppercase" align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg, index) => (
                  <TableRow
                    key={pkg.id}
                    onDragOver={(dragEvent) => dragEvent.preventDefault()}
                    onDrop={() => dropLevelRow(index)}
                    className={levelDragIndex === index ? "bg-gray-50" : ""}
                  >
                    <TableCell>
                      <span
                        draggable
                        onDragStart={() => setLevelDragIndex(index)}
                        onDragEnd={() => setLevelDragIndex(null)}
                        className="cursor-grab"
                      >
                        <DragIndicatorIcon sx={{ color: "#111827", fontSize: 16 }} />
                      </span>
                    </TableCell>
                    <TableCell className="!font-bold !text-gray-950">{pkg.code}</TableCell>
                    <TableCell className="!font-bold !text-gray-950">{pkg.name}</TableCell>
                    <TableCell align="center">{Number(pkg.includedTickets) || 0}</TableCell>
                    <TableCell>
                      <span className="font-extrabold text-black">
                        {formatDollars(pkg.price)}
                      </span>
                    </TableCell>
                    <TableCell align="center">{Number(pkg.maxAvailable) || 0}</TableCell>
                    <TableCell align="center">{totalAllocated(pkg)}</TableCell>
                    <TableCell align="right">
                      <div className="flex justify-end gap-2">
                        <WizardActionButton
                          tone="gold"
                          size="small"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => openEdit(pkg)}
                        >
                          Edit
                        </WizardActionButton>
                        <WizardActionButton
                          tone="red"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setPendingDelete(pkg)}
                        >
                          Delete
                        </WizardActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionShell>
      ) : null}

      <ConfirmDialog
        open={confirmDisableOpen}
        tone="warning"
        title="Turn off sponsorship opportunities?"
        message="Turning this off will remove the created sponsorship levels when you save."
        confirmLabel="Yes, turn off"
        onConfirm={() => {
          setConfirmDisableOpen(false);
          update("sponsorshipEnabled", false);
        }}
        onCancel={() => setConfirmDisableOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        tone="danger"
        title="Delete this sponsor level?"
        message={
          pendingDelete ? `"${pendingDelete.name}" will be removed from this event.` : undefined
        }
        confirmLabel="Yes, delete"
        onConfirm={() => {
          if (pendingDelete) {
            update(
              "sponsorshipPackages",
              packages.filter((item) => item.id !== pendingDelete.id)
            );
          }
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Add / Edit sponsor level dialog */}
      <Dialog open={Boolean(draft)} onClose={closeDraft} maxWidth="md" fullWidth>
        <DialogContent className="relative !p-8">
          <IconButton
            size="small"
            onClick={closeDraft}
            className="!absolute right-2 top-2 text-gray-400"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h3 className="text-[22px] font-extrabold leading-tight text-gray-950">
            Create Sponsor Levels
          </h3>
          <p className="mt-2 text-[16px] leading-6 text-gray-950">
            Enter the details for all the sponsor levels for this event.
          </p>
          {draft ? (
            <div className="mt-7 space-y-8">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <FieldLabel required>Sponsorships Available</FieldLabel>
                  <QuantityStepper
                    value={draft.maxAvailable}
                    onChange={(next) => patchDraft({ maxAvailable: next })}
                    ariaLabel="sponsorships available"
                  />
                </div>
                <div>
                  <FieldLabel required>Sponsor Level Code</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter Code"
                    value={draft.code || ""}
                    error={showValidation && !draft.code?.trim()}
                    onChange={({ target }) => patchDraft({ code: target.value })}
                    sx={wizardTextFieldSx}
                  />
                </div>
                <div>
                  <FieldLabel required>Sponsor Level Name</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter Sponsor Level Name"
                    value={draft.name}
                    error={showValidation && !draft.name.trim()}
                    onChange={({ target }) => patchDraft({ name: target.value })}
                    sx={wizardTextFieldSx}
                  />
                </div>
                <div>
                  <FieldLabel required>Sponsor Level Price</FieldLabel>
                  {/* POL-005: shared CurrencyInput (canonicalizes on blur, so
                      the old formatDollars onBlur hook is gone); verbatim
                      "Enter Price" placeholder; wizard-skin overrides keep the
                      58px MUI-outline look, with a red border standing in for
                      the MUI error state. */}
                  <CurrencyInput
                    ariaLabel="Sponsor Level Price"
                    placeholder="Enter Price"
                    value={draft.price}
                    onChange={(value) => patchDraft({ price: value })}
                    inputClassName={`${wizardCurrencyInputClassName} ${
                      showValidation && !draft.price.trim()
                        ? "!border-red-500"
                        : ""
                    }`}
                    adornmentClassName={wizardCurrencyAdornmentClassName}
                  />
                </div>
              </div>

              <div>
                <p className="text-[16px] font-extrabold leading-6 text-gray-950">
                  If enabled this sponsor level will appear on event registration page hero banner (limit 1). When sold Company Logo will appear.
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <Switch
                    checked={Boolean(draft.heroBannerEnabled)}
                    onChange={({ target }) => handleHeroBannerToggle(target.checked)}
                    inputProps={{ "aria-label": "Show this sponsor level on the hero banner" }}
                    sx={greenSwitchSx}
                  />
                  {/* Mock shows the ENABLED state label in green. */}
                  <span
                    className={`text-[14px] font-extrabold uppercase tracking-wide ${
                      draft.heroBannerEnabled ? "text-[#10B981]" : "text-gray-500"
                    }`}
                  >
                    {draft.heroBannerEnabled ? "ENABLED" : "DISABLED"}
                  </span>
                </div>
                {heroLimitBlocked ? (
                  <p className="mt-2 text-[14px] font-bold text-red-600">
                    Another sponsor level is already set to appear on the hero banner (limit 1). Turn it off on that sponsor level first.
                  </p>
                ) : null}
              </div>

              <div>
                <FieldLabel hint="Tickets allocated with this sponsorship, will show up on sponsorship invoice at no charge. If left blank no tickets will be assigned for this sponsorship.">
                  Assign Event Tickets (if applicable)
                </FieldLabel>
                {/* July 9, 2026 direction: only the No Charge Sponsorship
                    Ticket (NCST) can be assigned, so Code/Description/Price
                    are all greyed-out read-only and only the quantity is
                    editable. Code and Qty stay narrow so Description gets
                    the width. */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[110px_minmax(0,1fr)_150px_130px]">
                  <div>
                    <FieldLabel>Code</FieldLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={assignedTicket?.code || ""}
                      sx={wizardTextFieldSx}
                    />
                  </div>
                  <div>
                    <FieldLabel>Description</FieldLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={assignedTicket?.name || ""}
                      sx={wizardTextFieldSx}
                    />
                  </div>
                  <div>
                    <FieldLabel>Ticket Qty</FieldLabel>
                    <QuantityStepper
                      value={draft.includedTickets}
                      onChange={(next) => patchDraft({ includedTickets: next })}
                      ariaLabel="ticket quantity"
                      disabled={!assignedTicket}
                    />
                  </div>
                  <div>
                    <FieldLabel>Price</FieldLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={assignedTicket ? formatDollars(assignedTicket.price) : ""}
                      sx={wizardTextFieldSx}
                    />
                  </div>
                </div>
                {!assignedTicket ? (
                  <p className="mt-2 text-[14px] leading-6 text-gray-500">
                    This event has no No Charge Sponsorship Ticket (NCST). Add a ticket with code NCST in Ticket Set Up to include tickets with this sponsorship.
                  </p>
                ) : null}
              </div>

              <div>
                <FieldLabel hint="Enter the add-ons and quantities included with this sponsorship package. If left blank nothing will appear on website.">
                  Sponsorship Add-ons
                </FieldLabel>
                <WizardActionButton
                  tone="gold"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    // Mock's inline entry form opens with the stepper at 0.
                    setAddOnEntry({
                      id: wizardId("addon"),
                      description: "",
                      code: "",
                      quantity: "0",
                    })
                  }
                >
                  Add Sponsorship Add-ons
                </WizardActionButton>
                {addOnEntry ? (
                  <div className="mt-4 rounded-[10px] border border-gray-300 bg-white p-5">
                    {/* July 9, 2026 direction: Code renders before
                        Description, matching the add-ons list columns. */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_2fr_1fr_auto] md:items-end">
                      <div>
                        <FieldLabel>Code</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder="Enter Code"
                          value={addOnEntry.code}
                          onChange={({ target }) =>
                            setAddOnEntry({ ...addOnEntry, code: target.value })
                          }
                          sx={wizardTextFieldSx}
                        />
                      </div>
                      <div>
                        <FieldLabel>Description</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder="Enter Description"
                          value={addOnEntry.description}
                          onChange={({ target }) =>
                            setAddOnEntry({ ...addOnEntry, description: target.value })
                          }
                          sx={wizardTextFieldSx}
                        />
                      </div>
                      <div>
                        <FieldLabel>Quantity</FieldLabel>
                        <QuantityStepper
                          value={addOnEntry.quantity}
                          onChange={(next) => setAddOnEntry({ ...addOnEntry, quantity: next })}
                          ariaLabel="add-on quantity"
                        />
                      </div>
                      <WizardActionButton
                        tone="gold"
                        disabled={!addOnEntry.description.trim()}
                        onClick={saveAddOnEntry}
                      >
                        Save
                      </WizardActionButton>
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <FieldLabel hint="The list of sponsorship add-ons below will appear on website.">
                  Sponsorship Add-ons List
                </FieldLabel>
                <TableContainer className="rounded border border-gray-200">
                  <Table size="small">
                    <TableHead className="bg-gray-50">
                      <TableRow>
                        <TableCell className="!w-10" />
                        <TableCell className="!font-bold uppercase">Code</TableCell>
                        <TableCell className="!font-bold uppercase">
                          Add-ons Description
                        </TableCell>
                        <TableCell className="!font-bold uppercase" align="center">
                          Quantity
                        </TableCell>
                        <TableCell className="!font-bold uppercase" align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {draftAddOns.map((addOn, index) => (
                        <TableRow
                          key={addOn.id}
                          onDragOver={(dragEvent) => dragEvent.preventDefault()}
                          onDrop={() => dropAddOnRow(index)}
                          className={addOnDragIndex === index ? "bg-gray-50" : ""}
                        >
                          <TableCell>
                            <span
                              draggable
                              onDragStart={() => setAddOnDragIndex(index)}
                              onDragEnd={() => setAddOnDragIndex(null)}
                              className="cursor-grab"
                            >
                              <DragIndicatorIcon sx={{ color: "#111827", fontSize: 16 }} />
                            </span>
                          </TableCell>
                          <TableCell className="!font-bold !text-gray-950">{addOn.code}</TableCell>
                          <TableCell className="!font-bold !text-gray-950">
                            {addOn.description}
                          </TableCell>
                          <TableCell align="center">{addOn.quantity}</TableCell>
                          <TableCell align="right">
                            <WizardActionButton
                              tone="red"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() =>
                                patchDraft({
                                  addOns: draftAddOns.filter((item) => item.id !== addOn.id),
                                })
                              }
                            >
                              Delete
                            </WizardActionButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              <div>
                <FieldLabel
                  required
                  hint="List all benefits and recognition details sponsors will receive for this sponsorship which will be shown on website."
                >
                  Additional Sponsorship Benefits
                </FieldLabel>
                {/* Mock toolbar: Paragraph | B I U | bullet list | align |
                    link — the shared editor's bullet-list variant. */}
                <RichTextEditor
                  value={draft.additionalBenefits || ""}
                  onChange={(html) => patchDraft({ additionalBenefits: html })}
                  placeholder="Enter Sponsorship Benefits"
                  maxLength={2500}
                  minHeight={140}
                  withBulletList
                />
              </div>

              <div className="pt-2">
                {showValidation && !draftValid ? (
                  <p className="mb-3 text-[14px] font-bold text-red-600">
                    Please complete all required fields before saving.
                  </p>
                ) : null}
                <WizardActionButton tone="gold" onClick={saveDraft}>
                  Save Sponsor
                </WizardActionButton>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Step4SponsorSetup;
